import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Spin, Alert, message, DatePicker } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getTicket,
  createTicket,
  updateTicket,
  type CreateTicketDto,
  type UpdateTicketDto,
} from '@/api/ticketApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const TYPE_OPTIONS = ['inquiry', 'issue', 'feature_request'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const STATUS_OPTIONS = ['open', 'in_progress', 'pending', 'resolved', 'closed'];

interface TicketFormValues {
  subject: string;
  description?: string;
  type: string;
  priority: string;
  status?: string;
  assigned_to?: string;
  customer_id: string;
  contact_id?: string;
  tags?: string[];
  resolution?: string;
  sla_due_at?: dayjs.Dayjs;
}

export default function TicketForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<TicketFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getTicket(id)
      .then((ticket) => {
        form.setFieldsValue({
          subject: ticket.subject,
          description: ticket.description || undefined,
          type: ticket.type || 'inquiry',
          priority: ticket.priority || 'medium',
          status: ticket.status || 'open',
          assigned_to: ticket.assigned_to || undefined,
          customer_id: ticket.customer_id,
          contact_id: ticket.contact_id || undefined,
          tags: (ticket.tags?.length ?? 0) > 0 ? ticket.tags : undefined,
          resolution: ticket.resolution || undefined,
          sla_due_at: ticket.sla_due_at ? dayjs(ticket.sla_due_at) : undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: TicketFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateTicketDto = {
          subject: values.subject,
          description: values.description,
          type: values.type,
          priority: values.priority,
          status: values.status,
          assigned_to: values.assigned_to,
          customer_id: values.customer_id,
          contact_id: values.contact_id,
          tags: values.tags,
          resolution: values.resolution,
          sla_due_at: values.sla_due_at?.toISOString(),
        };
        await updateTicket(id, dto);
        message.success(t('tickets.update_success'));
        navigate(`/tickets/${id}`);
      } else {
        const dto: CreateTicketDto = {
          subject: values.subject,
          description: values.description,
          type: values.type,
          priority: values.priority,
          assigned_to: values.assigned_to,
          customer_id: values.customer_id,
          contact_id: values.contact_id,
          tags: values.tags,
          sla_due_at: values.sla_due_at?.toISOString(),
        };
        const created = await createTicket(dto);
        message.success(t('tickets.create_success'));
        navigate(`/tickets/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('tickets', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/tickets')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('tickets.edit_title') : t('tickets.create_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ type: 'inquiry', priority: 'medium' }}
        style={{ maxWidth: 700 }}
      >
        <Form.Item
          name="subject"
          label={t('tickets.field.subject')}
          rules={[
            { required: true, message: t('tickets.validation.subject_required') },
            { max: 500, message: t('tickets.validation.subject_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('tickets.field.description')}
          rules={[{ max: 5000, message: t('tickets.validation.description_max_length') }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('tickets.field.type')}
          rules={[{ required: true, message: t('tickets.validation.type_required') }]}
        >
          <Select>
            {TYPE_OPTIONS.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {t(`tickets.type_options.${opt}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="priority"
          label={t('tickets.field.priority')}
          rules={[{ required: true, message: t('tickets.validation.priority_required') }]}
        >
          <Select>
            {PRIORITY_OPTIONS.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {t(`tickets.priority_options.${opt}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('tickets.field.status')}>
            <Select>
              {STATUS_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>
                  {t(`tickets.status_options.${opt}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="customer_id"
          label={t('tickets.field.customer_id')}
          rules={[{ required: true, message: t('tickets.validation.customer_id_required') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="contact_id" label={t('tickets.field.contact_id')}>
          <Input />
        </Form.Item>

        <Form.Item name="assigned_to" label={t('tickets.field.assigned_to')}>
          <Input />
        </Form.Item>

        <Form.Item name="tags" label={t('tickets.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        {isEdit && (
          <Form.Item name="resolution" label={t('tickets.field.resolution')}>
            <TextArea rows={3} />
          </Form.Item>
        )}

        <Form.Item name="sla_due_at" label={t('tickets.field.sla_due_at')}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/tickets')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
